import React, { createContext, useContext, useState, useEffect } from "react";
import { api } from "../services/api";

interface User {
  _id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isDemoMode: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  enterDemoMode: () => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isDemoMode, setIsDemoMode] = useState<boolean>(false);

  useEffect(() => {
    // Check for saved auth data on mount
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    const savedDemoMode = localStorage.getItem("demoMode");

    if (savedDemoMode === "true") {
      setIsDemoMode(true);
      setUser({
        _id: "demo-user-id",
        email: "demo@example.com",
        name: "DEMO",
      });
      setToken("demo-token");
    } else if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.login(email, password);
      setUser(response.user);
      setToken(response.token);
      setIsDemoMode(false);
      localStorage.setItem("token", response.token);
      localStorage.setItem("user", JSON.stringify(response.user));
      localStorage.removeItem("demoMode");
    } catch (error) {
      throw new Error("Login failed");
    }
  };

  const register = async (email: string, password: string, name: string) => {
    try {
      const response = await api.register(email, password, name);
      setUser(response.user);
      setToken(response.token);
      setIsDemoMode(false);
      localStorage.setItem("token", response.token);
      localStorage.setItem("user", JSON.stringify(response.user));
      localStorage.removeItem("demoMode");
    } catch (error) {
      throw new Error("Registration failed");
    }
  };

  const enterDemoMode = () => {
    setUser({
      _id: "demo-user-id",
      email: "demo@example.com",
      name: "DEMO",
    });
    setToken("demo-token");
    setIsDemoMode(true);
    localStorage.setItem("demoMode", "true");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsDemoMode(false);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("demoMode");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isDemoMode,
        login,
        register,
        enterDemoMode,
        logout,
        isAuthenticated: !!user && !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
